import { useEffect, useState } from "react";
import { CheckCircle, Mail, Clock2, ShieldCheck, Wallet } from "lucide-react";
import { Link } from "react-router-dom";
import { useGetConnectedAccount } from "../../../hooks/api/Tutors/Stripe/useGetConnectedAccount";

const VerificationProgress = ({ tutor }: { tutor: any }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const emailVerified = tutor?.data?.isEmailVerified === true;
  const hasAvailability =
    Array.isArray(tutor?.data?.weeklyAvailability) &&
    tutor.data?.weeklyAvailability.some(
      (day: any) => Array.isArray(day.timeSlots) && day.timeSlots.length > 0,
    );
  const availabilitySet = hasAvailability;
  const stripeOnboarded = tutor?.data?.isStripeOnboarded === true;
  const accountVerified = tutor?.data?.isAccountVerified === true;

  useEffect(() => {
    let step = 0;

    if (emailVerified) step = 1;
    if (emailVerified && availabilitySet) step = 2;
    if (emailVerified && availabilitySet && accountVerified) step = 3;
    if (emailVerified && availabilitySet && accountVerified && stripeOnboarded)
      step = 4;

    setCurrentStep(step);
  }, [emailVerified, availabilitySet, accountVerified, stripeOnboarded]);

  // Stripe popup logic
  const [verify, setVerify] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [clicked, setClicked] = useState(false);

  const { isLoading } = useGetConnectedAccount({
    trigger: verify,
    hasOpened,
    setHasOpened,
  });

  const handleClick = () => {
    setClicked(true);
    if (!isOpening) {
      setIsOpening(true);
      setVerify(true);
      setTimeout(() => {
        setIsOpening(false);
      }, 5000);
    }
  };

  const steps = [
    {
      title: "Complete Profile Setup",
      description: "Verify your email",
      icon: <Mail />,
    },
    {
      title: "Set Availability",
      description: "Set your availability for tutoring",
      icon: <Clock2 />,
    },
    {
      title: "Admin Approval",
      description: "Wait for an admin to verify your account",
      icon: <ShieldCheck />,
    },
    {
      title: "Setup Stripe",
      description: "Connect your Stripe account for payouts",
      icon: <Wallet />,
    },
  ];

  const totalSteps = steps.length;
  const progressWidth = `${(currentStep / totalSteps) * 100}%`;

  return (
    <div className="p-5 bg-white rounded-md border border-primary500 mt-2">
      <h3 className="text-gray900 W2STMedium">
        Profile Completeness: {Math.round((currentStep / totalSteps) * 100)}%
      </h3>
      <p className="text-gray500 mt-2">
        Complete the process to fully enjoy all the benefits of your tutor
        profile.
      </p>

      <div className="bg-gray-200 rounded-md mt-5 h-2 w-full">
        <div
          className="bg-primary600 h-2 rounded-md transition-all"
          style={{ width: progressWidth }}
        ></div>
      </div>

      {steps.map((step, index) => {
        const isStepCompleted = (() => {
          if (index === 0) return emailVerified;
          if (index === 1) return emailVerified && availabilitySet;
          if (index === 2) return accountVerified;
          if (index === 3) return stripeOnboarded;
          return false;
        })();

        return (
          <div
            key={index}
            className={`flex items-center justify-between py-4 border-b ${
              isStepCompleted ? "opacity-50 line-through" : ""
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`p-2 rounded-md ${
                  isStepCompleted
                    ? "bg-green-500 text-white"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                {isStepCompleted ? <CheckCircle size={20} /> : step.icon}
              </div>
              <div>
                <h3 className="W2STMedium text-gray-900">{step.title}</h3>
                <p className="text-sm text-gray-500">{step.description}</p>
              </div>
            </div>

            {index === 1 && (
              <Link
                to={index === currentStep ? "/tutor/availability" : "#"}
                onClick={(e) => {
                  if (index !== currentStep) e.preventDefault();
                }}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  index === currentStep
                    ? "bg-black text-white"
                    : "bg-gray-200 text-gray-500 cursor-not-allowed pointer-events-none"
                }`}
              >
                Start
              </Link>
            )}

            {index === 2 && (
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  accountVerified
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-700"
                }`}
              >
                <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-current"></span>
                {accountVerified ? "Verified" : "Awaiting admin verification"}
              </span>
            )}

            {index === 3 && (
              <button
                disabled={
                  currentStep !== 3 ||
                  isLoading ||
                  isOpening ||
                  clicked ||
                  tutor?.data?.isStripeOnboarded
                }
                onClick={handleClick}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  tutor?.data?.isStripeOnboarded
                    ? "bg-gray-200 text-gray-500 cursor-not-allowed pointer-events-none"
                    : currentStep === 3
                      ? "bg-black text-white"
                      : "bg-gray-200 text-gray-500 cursor-not-allowed pointer-events-none"
                }`}
              >
                {tutor?.data?.isStripeOnboarded ? "Completed" : "Start"}
              </button>
            )}
          </div>
        );
      })}
      {/* {currentStep === 2 && tutorData?.data && (
        <div className="mt-6 rounded-lg border border-gray-200 bg-white shadow-sm transition-all hover:shadow-md">
          <div className="border-b border-gray-100 bg-gray-50/50 p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary100">
                <ShieldCheck className="h-5 w-5 text-primary600" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-900">
                  Background Check Status
                </h4>
                <p className="text-xs text-gray-500">
                  Real-time updates from our verification partner
                </p>
              </div>
            </div>
          </div>
          <div className="grid gap-6 p-4 sm:grid-cols-2">
            <div className="rounded-md border border-gray-100 bg-gray-50 p-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">
                Current Status
              </p>
              <div className="flex items-center">
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${(() => {
                    const value = tutorData.data.checkrReportStatus || "";
                    const normalized = value.toLowerCase();
                    if (normalized === "clear" || normalized === "completed") {
                      return "bg-green-100 text-green-800";
                    }
                    if (normalized === "pending") {
                      return "bg-yellow-100 text-yellow-800";
                    }
                    if (
                      normalized === "consider" ||
                      normalized === "disputed"
                    ) {
                      return "bg-orange-100 text-orange-800";
                    }
                    if (normalized === "suspended") {
                      return "bg-red-100 text-red-800";
                    }
                    return "bg-gray-100 text-gray-700";
                  })()}`}
                >
                  <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-current"></span>
                  {tutorData.data.checkrReportStatus || "Not Started"}
                </span>
              </div>
            </div>
            <div className="rounded-md border border-gray-100 bg-gray-50 p-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500">
                Screening Result
              </p>
              <div className="flex items-center">
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${(() => {
                    const value = tutorData.data.checkrReportResult || "";
                    const normalized = value.toLowerCase();
                    if (normalized === "clear") {
                      return "bg-green-100 text-green-800";
                    }
                    if (normalized === "consider") {
                      return "bg-orange-100 text-orange-800";
                    }
                    if (normalized === "suspended") {
                      return "bg-red-100 text-red-800";
                    }
                    return "bg-gray-100 text-gray-700";
                  })()}`}
                >
                  <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-current"></span>
                  {tutorData.data.checkrReportResult || "Pending"}
                </span>
              </div>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default VerificationProgress;
